/**
 * Export Email Modal — confirm a destination email, then trigger the server-side
 * export and tell the user to check their inbox.
 *
 * The export is generated server-side (streaming XLSX → Cloud Storage) and the
 * download link(s) are emailed. Used from the client Dashboard and the admin
 * ManageClient page. Pre-fills the destination with `defaultEmail` when given
 * (admin panel), otherwise fetches the target client's on-file email.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../services/firebase';
import { useAuth } from '../../hooks/useAuth';

interface ExportEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportType: string;        // 'all' | 'good_all' | classification key
  exportLabel: string;       // human label for the header (e.g. "All Calls")
  clientId?: string | null;  // passed when impersonating or from the admin panel
  defaultEmail?: string;     // admin override; otherwise the modal fetches the client email
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || '').trim());
}

export function ExportEmailModal({ isOpen, onClose, exportType, exportLabel, clientId, defaultEmail }: ExportEmailModalProps) {
  const { user } = useAuth();
  const [phase, setPhase] = useState<'confirm' | 'sending' | 'done' | 'error'>('confirm');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetClientId = clientId || user?.uid || '';

  // Pre-fill the destination each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setPhase('confirm');
    setMessage(null);
    setError(null);

    if (defaultEmail) {
      setEmail(defaultEmail);
      return;
    }
    // Fetch the target client's on-file email (client reads own doc; admin any).
    let cancelled = false;
    setEmail('');
    (async () => {
      try {
        if (!targetClientId) return;
        const snap = await getDoc(doc(db, 'clients', targetClientId));
        const fetched = (snap.data()?.email as string) || user?.email || '';
        if (!cancelled) setEmail(fetched);
      } catch {
        if (!cancelled) setEmail(user?.email || '');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultEmail, targetClientId]);

  if (!isOpen) return null;

  const handleSend = async () => {
    setPhase('sending');
    setError(null);
    try {
      const fn = httpsCallable(functions, 'exportResults', { timeout: 540000 });
      const response = await fn({
        exportType,
        ...(clientId ? { clientId } : {}),
        recipientEmail: email.trim(),
      });
      const data = response.data as { message: string };
      setMessage(data.message);
      setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
      setPhase('error');
    }
  };

  const handleClose = () => {
    setPhase('confirm');
    setMessage(null);
    setError(null);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '460px', width: '90%', border: '1px solid var(--dark-3)',
      }} onClick={e => e.stopPropagation()}>

        {(phase === 'confirm' || phase === 'sending' || phase === 'error') && (
          <>
            <h2 style={{ margin: '0 0 8px', color: 'var(--white)' }}>Export {exportLabel}</h2>
            <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px', lineHeight: 1.5 }}>
              We&rsquo;ll prepare the full export and email a secure download link. The file is an
              Excel spreadsheet and the link stays active for 7 days.
            </p>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ color: 'var(--gray-400)', fontSize: '0.85rem', display: 'block', marginBottom: '6px' }}>
                Send export to:
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={phase === 'sending'}
                placeholder="name@example.com"
                autoFocus
                style={{
                  width: '100%', padding: '10px', background: 'var(--dark)',
                  border: '1px solid var(--dark-3)', borderRadius: '6px',
                  color: 'var(--white)', fontFamily: 'inherit', fontSize: '1rem',
                }}
              />
            </div>

            {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: '12px' }}>{error}</p>}

            {phase === 'sending' ? (
              <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                <div style={{ color: 'var(--gold)', fontSize: '1rem', marginBottom: '4px' }}>Preparing your export…</div>
                <p style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>
                  This can take up to a minute for large accounts. Please keep this window open.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={handleClose} className="btn btn-secondary" style={{ flex: 1 }}>
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={!isValidEmail(email)}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  Send Export
                </button>
              </div>
            )}
          </>
        )}

        {phase === 'done' && (
          <>
            <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
              <div style={{ color: 'var(--green, #10b981)', fontSize: '1.1rem', marginBottom: '8px' }}>
                Check your email
              </div>
              <p style={{ color: 'var(--gray-300)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                {message || `Your export is on its way to ${email}.`}
              </p>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginTop: '10px' }}>
                The download link will be available for 7 days.
              </p>
            </div>
            <button onClick={handleClose} className="btn btn-primary" style={{ width: '100%', marginTop: '16px' }}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
